import { Info } from 'lucide-react'
import { useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Markdown from 'react-markdown'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { parseError } from '@/lib/errors'
import { formatDateTime } from '@/lib/formatters'
import { type SalesLead } from '@/models/salesLead'
import {
  activeSequence,
  type Sequence,
  SequenceStatus,
  SequenceStepType,
  useAddStep,
  useApplySequenceTemplate,
  useCreateSequence,
  useDeleteStep,
  useLeadSequences,
  usePauseSequence,
  useReorderSteps,
  useResumeSequence,
  useStartFromLast,
  useStartSequence,
  useStopSequence,
  useUpdateStep,
} from '@/models/sequence'
import { AddStepMenu } from './Sequence/AddStepMenu'
import { EmailStepEditor } from './Sequence/EmailStepEditor'
import { SequenceStepList } from './Sequence/SequenceStepList'
import { SequenceTemplateSelect } from './Sequence/SequenceTemplateSelect'
import { SmsStepEditor } from './Sequence/SmsStepEditor'
import { WaitStepEditor } from './Sequence/WaitStepEditor'

const onErr = (e: unknown) => toast.error(parseError(e))

function formatTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function combineDateTime(date: Date, time: string): Date {
  const [h, m] = time.split(':').map(Number)
  const d = new Date(date)
  d.setHours(h || 0, m || 0, 0, 0)
  return d
}

interface SequencesTabProps {
  lead: SalesLead
}

export function SequencesTab({ lead }: SequencesTabProps) {
  const { data: sequences = [] } = useLeadSequences(lead.id)
  const active = activeSequence(sequences)
  const createSeq = useCreateSequence(lead.id)
  const startFromLast = useStartFromLast(lead.id)

  if (!active) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p className="text-muted-foreground text-sm">No sequence for this lead yet.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button className="text-md" onClick={() => createSeq.mutate(undefined, { onError: onErr })}>
            New sequence
          </Button>
          <Button
            variant="outline"
            className="text-md"
            onClick={() => startFromLast.mutate(undefined, { onError: onErr })}
          >
            Start from last sequence
          </Button>
          <SequenceTemplateSelect
            className="w-64"
            onSelect={(template) => createSeq.mutate(template.id, { onError: onErr })}
          />
        </div>
      </div>
    )
  }
  return <ActiveSequence sequence={active} />
}

// stepId set => editing an existing step; absent => composing a new one.
type Draft =
  | { stepId?: string; type: SequenceStepType.SEND_EMAIL; subject: string; body: string }
  | { stepId?: string; type: SequenceStepType.SEND_SMS; body: string }
  | { stepId?: string; type: SequenceStepType.WAIT; days: number }
  | null

function ActiveSequence({ sequence }: { sequence: Sequence }) {
  const addStep = useAddStep()
  const updateStep = useUpdateStep()
  const deleteStep = useDeleteStep()
  const reorder = useReorderSteps()
  const applyTemplate = useApplySequenceTemplate()
  const start = useStartSequence()
  const stop = useStopSequence()
  const pause = usePauseSequence()
  const resume = useResumeSequence()

  const [draft, setDraft] = useState<Draft>(null)
  const [execDate, setExecDate] = useState<Date | undefined>(() => new Date(sequence.dateExecuteStart))
  const [execTime, setExecTime] = useState(() => formatTime(new Date(sequence.dateExecuteStart)))

  const isDraft = sequence.status === SequenceStatus.DRAFT
  const isRunning = sequence.status === SequenceStatus.RUNNING
  const isPaused = sequence.status === SequenceStatus.PAUSED
  // Steps can be reorganised/added/removed while the sequence is live (DRAFT/RUNNING/PAUSED).
  const editable = isDraft || isRunning || isPaused

  function onAdd(type: SequenceStepType) {
    if (type === SequenceStepType.SEND_EMAIL) {
      setDraft({ type, subject: '', body: '' })
    } else if (type === SequenceStepType.SEND_SMS) {
      setDraft({ type, body: '' })
    } else if (type === SequenceStepType.WAIT) {
      setDraft({ type, days: 3 })
    } else {
      addStep.mutate({ sequence: sequence.id, stepType: type, config: {} }, { onError: onErr })
    }
  }

  function onEdit(id: string) {
    const step = sequence.steps.find((s) => s.id === id)
    if (!step) return
    if (step.stepType === SequenceStepType.SEND_EMAIL) {
      setDraft({
        stepId: id,
        type: SequenceStepType.SEND_EMAIL,
        subject: step.config.subject ?? '',
        body: step.config.body ?? '',
      })
    } else if (step.stepType === SequenceStepType.SEND_SMS) {
      setDraft({ stepId: id, type: SequenceStepType.SEND_SMS, body: step.config.body ?? '' })
    } else if (step.stepType === SequenceStepType.WAIT) {
      setDraft({ stepId: id, type: SequenceStepType.WAIT, days: step.config.days ?? 3 })
    }
  }

  function saveDraft() {
    if (!draft) return
    const config =
      draft.type === SequenceStepType.WAIT
        ? { days: draft.days }
        : draft.type === SequenceStepType.SEND_SMS
          ? { body: draft.body }
          : {
              subject: draft.subject,
              body: draft.body,
              // Render markdown -> HTML now (same as the live email composer) so the
              // engine can send a formatted email; tokens stay literal for server-side render.
              htmlBody: renderToStaticMarkup(<Markdown>{draft.body}</Markdown>),
            }
    const done = { onSuccess: () => setDraft(null), onError: onErr }
    if (draft.stepId) {
      updateStep.mutate({ id: draft.stepId, config }, done)
    } else {
      addStep.mutate({ sequence: sequence.id, stepType: draft.type, config }, done)
    }
  }

  const savingStep = addStep.isPending || updateStep.isPending

  return (
    <div className="flex flex-col gap-3 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Badge variant="outline">{sequence.status}</Badge>
          {(isRunning || isPaused) && (
            <span className="text-muted-foreground text-sm">
              Start execution: {formatDateTime(sequence.dateExecuteStart)}
            </span>
          )}
        </div>
        {isDraft && (
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">Start execution</span>
              <div className="flex items-end gap-2">
                <DateInput value={execDate} onChange={setExecDate} />
                <Input
                  type="time"
                  value={execTime}
                  onChange={(e) => setExecTime(e.target.value)}
                  className="w-28"
                  aria-label="Start time"
                />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                className="text-md"
                disabled={!execDate}
                onClick={() => {
                  if (!execDate) return
                  start.mutate(
                    {
                      id: sequence.id,
                      dateExecuteStart: combineDateTime(execDate, execTime).toISOString(),
                    },
                    { onError: onErr }
                  )
                }}
              >
                Start sequence
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="iconOnly"
                    size="icon-sm"
                    aria-label="When execution starts"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Info className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Execution begins at the next half-hour mark (:00 or :30) after your chosen time, when the
                  scheduler next runs.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        {isRunning && (
          <Button variant="outline" onClick={() => pause.mutate(sequence.id, { onError: onErr })}>
            Pause
          </Button>
        )}
        {isPaused && (
          <div className="flex gap-2">
            <Button onClick={() => resume.mutate(sequence.id, { onError: onErr })}>Resume</Button>
            <Button variant="destructive" onClick={() => stop.mutate(sequence.id, { onError: onErr })}>
              Stop
            </Button>
          </div>
        )}
      </div>

      {/* Hidden mid-edit: replacing the steps would strand the open editor on a deleted step. */}
      {isDraft && !draft && (
        <SequenceTemplateSelect
          className="max-w-sm"
          confirmReplace={sequence.steps.length > 0}
          onSelect={(template) =>
            applyTemplate.mutate({ id: sequence.id, templateId: template.id }, { onError: onErr })
          }
        />
      )}

      <SequenceStepList
        steps={sequence.steps}
        reorderable={editable}
        onReorder={(orderedIds) => reorder.mutate({ id: sequence.id, orderedIds }, { onError: onErr })}
        onEditStep={editable ? onEdit : undefined}
        onDeleteStep={(id) => deleteStep.mutate(id, { onError: onErr })}
      />

      {draft?.type === SequenceStepType.SEND_EMAIL && (
        <EmailStepEditor
          subject={draft.subject}
          body={draft.body}
          onChange={(next) => setDraft({ stepId: draft.stepId, type: SequenceStepType.SEND_EMAIL, ...next })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saving={savingStep}
          saveLabel={draft.stepId ? 'Save step' : 'Add step'}
          prefill={!draft.stepId}
        />
      )}
      {draft?.type === SequenceStepType.SEND_SMS && (
        <SmsStepEditor
          body={draft.body}
          onChange={(body) => setDraft({ stepId: draft.stepId, type: SequenceStepType.SEND_SMS, body })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saving={savingStep}
          saveLabel={draft.stepId ? 'Save step' : 'Add step'}
          prefill={!draft.stepId}
        />
      )}
      {draft?.type === SequenceStepType.WAIT && (
        <WaitStepEditor
          days={draft.days}
          onChange={(days) => setDraft({ stepId: draft.stepId, type: SequenceStepType.WAIT, days })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saving={savingStep}
          saveLabel={draft.stepId ? 'Save' : 'Add'}
        />
      )}

      {!draft && <AddStepMenu onAdd={onAdd} />}
    </div>
  )
}
